// =============================================================================
// 採取資源ノード。島の地形高さ場を使い、ビーチ〜低地に決定論的に散布する。
// 3Dシーンは改変せず、HUDがDOMマーカーとして投影表示する（projection.ts）。
// =============================================================================
import type { Heightfield, NodeDef, ResourceNode } from './types';

// ノード種別 → 産出アイテム・表示
export const NODE_TYPES: Record<string, NodeDef> = {
  driftwood: { item: 'wood',    icon: '🪵', label: '流木',     yield: 2, reach: 4.5 },
  palm:      { item: 'coconut', icon: '🌴', label: 'ヤシの木', yield: 3, reach: 5.0, extra: { item: 'wood', count: 1 } },
  rock:      { item: 'stone',   icon: '🪨', label: '岩',       yield: 3, reach: 4.5 },
  bush:      { item: 'fiber',   icon: '🌿', label: '茂み',     yield: 2, reach: 4.0 },
};

// 決定論 LCG
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => { s = (1103515245 * s + 12345) & 0x7fffffff; return s / 0x7fffffff; };
}

// 地形高さ場から、上陸できる低地セルを集めてノードを散布
export function placeResourceNodes(
  hf: Heightfield | null,
  opts: { seed?: number; minSep?: number; count?: number } = {},
): ResourceNode[] {
  const nodes: ResourceNode[] = [];
  if (!hf) return nodes;
  const rng = makeRng(opts.seed || 1337);
  const N = hf.N;

  // 候補セル: H が [0.4, 12]（海面のすぐ上〜低地）の地表
  const cand: { x: number; z: number; h: number }[] = [];
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const h = hf.H[j * N + i];
      if (h > 0.4 && h < 12) {
        const x = hf.minX + (i + 0.5) * hf.cellX;
        const z = hf.minZ + (j + 0.5) * hf.cellZ;
        cand.push({ x, z, h });
      }
    }
  }
  // シャッフル
  for (let i = cand.length - 1; i > 0; i--) { const k = Math.floor(rng() * (i + 1)); const t = cand[i]; cand[i] = cand[k]; cand[k] = t; }

  const types = ['driftwood', 'palm', 'rock', 'bush', 'driftwood', 'rock', 'bush', 'palm'];
  const minSep = opts.minSep || 11;
  const want = opts.count || 40;
  let ti = 0;
  for (const c of cand) {
    if (nodes.length >= want) break;
    let ok = true;
    for (const n of nodes) { if (Math.hypot(n.x - c.x, n.z - c.z) < minSep) { ok = false; break; } }
    if (!ok) continue;
    const type = types[ti++ % types.length];
    const def = NODE_TYPES[type];
    nodes.push({ id: nodes.length, type, x: c.x, y: c.h, z: c.z, amount: def.yield, def });
  }
  return nodes;
}

// spawn地点(最も低いノード=spawnOnBeachの着地点)の近くに、各資源タイプが最低1個
// あることを保証する。島メッシュのロードタイミングで配置がぶれても「繊維(茂み)だけ
// 遠くて見つからない」状態を防ぐ。足りないタイプは近場の余剰タイプのノードを付け替える。
export function ensureTypesNearSpawn(nodes: ResourceNode[], radius = 140): void {
  if (!nodes.length) return;
  let anchor = nodes[0];
  for (const n of nodes) if (n.y < anchor.y) anchor = n; // spawnOnBeach と同じ基準
  const distA = (n: ResourceNode) => Math.hypot(n.x - anchor.x, n.z - anchor.z);
  for (const t of Object.keys(NODE_TYPES)) {
    if (nodes.some((n) => n.type === t && distA(n) <= radius)) continue;
    // radius内に各種別が何個あるか（唯一の近接ノードは奪わない＝新たな穴を作らない）
    const inRadius: Record<string, number> = {};
    for (const n of nodes) if (distA(n) <= radius) inRadius[n.type] = (inRadius[n.type] || 0) + 1;
    let cand: ResourceNode | null = null, bd = Infinity;
    for (const n of nodes) {
      if (n === anchor || n.type === t || (inRadius[n.type] || 0) <= 1) continue; // spawn地点は変えない
      const d = distA(n);
      if (d <= radius && d < bd) { bd = d; cand = n; } // radius内の余剰種別から最寄りを付け替え
    }
    if (!cand) { // radius内に余剰が無ければ、全体で余剰(残3個以上)種別の最寄り(anchor除く)
      const counts: Record<string, number> = {};
      for (const n of nodes) counts[n.type] = (counts[n.type] || 0) + 1;
      for (const n of nodes) {
        if (n === anchor || n.type === t || (counts[n.type] || 0) <= 2) continue;
        const d = distA(n);
        if (d < bd) { bd = d; cand = n; }
      }
    }
    if (cand) { const def = NODE_TYPES[t]; cand.type = t; cand.def = def; cand.amount = def.yield; }
  }
}

// プレイヤーに最も近い、リーチ内の生存ノード。reachMulで身長スケールに合わせる。
export function nearestNode(nodes: ResourceNode[], px: number, py: number, pz: number, reachMul = 1): ResourceNode | null {
  let best: ResourceNode | null = null, bestD = Infinity;
  for (const n of nodes) {
    if (n.amount <= 0) continue;
    const d = Math.hypot(n.x - px, n.z - pz);
    if (d < n.def.reach * reachMul && Math.abs(n.y - py) < 8 * reachMul && d < bestD) { bestD = d; best = n; }
  }
  return best;
}

// 採取。産出アイテムと付随物を返す。
export function gatherNode(node: ResourceNode | null): { item: string; count: number }[] | null {
  if (!node || node.amount <= 0) return null;
  node.amount -= 1;
  const out = [{ item: node.def.item, count: 1 }];
  if (node.def.extra) out.push({ item: node.def.extra.item, count: node.def.extra.count });
  return out;
}
